<template lang="pug">
	.creative-excel-data.flex-col.space
		.flex-row.space.justify.align-center
			.flex-row.space
				slot(name="header-left", :allowManaging="allowManaging")
			.flex-row.space
				slot(name="header-right", :allowManaging="allowManaging")
				Switcher(v-if="allowManaging", :value.sync="allowMultipleExcelData", :text="$tCaption('Multiple upload')")
		FileUploader(ref="uploader", :isUIHidden="!allowUploadButton", @upload="onFileUpload", @file-selected="onFileSelected", :text="$tCaption('Upload new excel file')", :accept="['.xlsx']", @ui-button-click="clear",
			:uploadText="replaceDataState === 0 ? $tCaption('Next', '@actions') : $tCaption('Replace data')", :preventClose="true", :disabled="!!isLoading")
			template(v-if="replaceDataFile && replaceDataState === 1")
				.flex-col.space-medium
					DxCheckBox(:text="$tCaption('Add to existing sub-tracks')", :value.sync="addToExisting")
					.upper.grey {{ $tCaption('Root insertions') }}
					.flex-col.space-medium
						.flex-row.space-large(v-for="info in insertionsInfo")
							.flex-col.space
								.font-small.upper.grey {{$tCaption('Root track')}}
								span {{info.track.name().substr(0, 40)}}
							template(v-if="replaceDataFile && !replaceDataFile.columns")
								.flex-col
									.font-small.upper.grey {{$tCaption('Key column')}}
									DxSelectBox(:value="info.settings.keyColumn", :data-source="columns", :validationStatus="!info.settings.keyColumn ? 'invalid' : 'valid'", @value-changed="e => onKeyColumnChange(e, info.settings, columns)")
								.flex-col.center
									.font-small.upper.grey(v-html="'&nbsp'")
									DxCheckBox(:text="$tCaption('Calc geo-location')", :value="info.settings.calcGeoLocation", @value-changed="e => $set(info.settings, 'calcGeoLocation', e.value)")
		.flex-col.space-large(v-if="excelData.length")
			.flex-col.space(v-for="(file, fileIdx) in excelData")
				.flex-row.space.justify
					.font-big.name.flex-row.align-center {{file.name}}
					.actions.flex-row.align-center.space.justify-end
						DxButton(v-if="allowManaging", icon="refresh", :text="$tCaption('Replace data', '@actions')", @click="replaceData(file)", stylingMode="text")
						DxButton(icon="trash", :text="$tCaption('Delete', '@actions')", @click="removeFile(file, fileIdx)", stylingMode="text", type="danger", v-if="allowManaging")
						slot(name="actions", :data="{ file }")
				.flex-row.space-large.align-center
					template(v-if="file.columns")
						.flex-col
							.font-small.upper.grey {{$tCaption('Key column')}}
							DxSelectBox(:value="file.keyColumn", :data-source="file.columns", :validationStatus="!file.keyColumn ? 'invalid' : 'valid'", 
								@value-changed="e => onKeyColumnChange(e, file, file.columns)", :disabled="!allowManaging", width="120")
						.flex-col.space.align-end(v-if="hasAddressColumn(file)")
							.font-small.upper.grey {{$tCaption('Use address column for calculation')}}
							DxCheckBox(:value="!!file.calcGeoLocation", @value-changed="e => $set(file, 'calcGeoLocation', e.value)", :disabled="!allowManaging")
						.flex-col.space.align-end(v-if="file.dateFormat")
							.font-small.upper.grey {{$tCaption('Date format')}}
							span {{file.dateFormat}}
					.flex-col.space
						.font-small.upper.grey {{$tCaption('Uploaded')}}
						.created.flex-row.align-center.justify-end {{$moment(file.updated || file.created).format('l')}}
					.flex-col.space
						.font-small.upper.grey {{$tCaption('Used in root clips')}}
						.used.flex-row.align-center.justify-end {{file.insertions && file.insertions.length || 0}}
				
</template>

<script>
	import axios from 'axios';
	import _ from 'lodash';
	import FileUploader from 'app-core/FileUploader.vue';
	import DxButton from 'devextreme-vue/button';
	import DxCheckBox from 'devextreme-vue/check-box';
	import DxSelectBox from 'devextreme-vue/select-box';
	import ssmlHelper from 'view/insertion/ssml-helper';
	import { readColumns, getUsedColumnsInSsml } from './excel-data-helper';
	import Switcher from 'app-core/Switcher.vue';

	export default {
		translationCategory: 'creative.CreativeExcelData',
		name: 'creative-excel-data',
		components: {
			FileUploader, DxButton, DxCheckBox, DxSelectBox, Switcher
		},
		props: ['creative', 'allowManaging', 'allowUpload', 'tracker', 'multiple'],
		data() {
			return {
				isLoading: 0,
				replaceDataFile: null,
				addToExisting: false,
				insertionsInfo: [],
				replaceDataState: 0,
				columns: []
			};
		},
		computed: {
			excelData() {
				return this.creative.JsonConfig.excelData || [];
			},
			tracks() {
				return this.getTracks(this.replaceDataFile);
			},
			allowUploadButton() {
				return this.allowUpload && (this.multiple || !this.excelData.length || this.allowMultipleExcelData);
			},
			settingsVisible() {
				return this.excelData.some(f => f.columns);
			},
			allowMultipleExcelData: {
				get: function() {
					return this.creative.JsonConfig.allowMultipleExcelData || false;
				},
				set: function(v) {
					this.$set(this.creative.JsonConfig, 'allowMultipleExcelData', v);
				}
			}
		},
		mounted() {
			if (__DEBUG__)
				window.creativeExcelFile = this;
		},
		methods: {
			hasAddressColumn(excel) {
				return excel.columns?.some(f => f === 'Address');
			},
			getTracks(file) {
				if (!file) return [];
				return this.tracker.tracks?.filter(f => file.insertions.find(i => i.insertionId === f.id) || f.insertion.JsonConfig.subTracksGenerationSettings?.excelDataId === file?.id)
					.filter(f => f.insertion.JsonConfig.subTracksGenerationSsmlTemplate && f.insertion.JsonConfig.subTracksGenerationSettings);
			},
			onKeyColumnChange(e, info, columns) {
				this.$set(info, 'keyColumn', e.value);
				this.$set(info, 'keyColumnIndex', columns.findIndex(f => f === e.value));
			},
			loadInsertionsInfo() {
				this.insertionsInfo = this.tracks.map(f => ({
					track: f,
					insertion: f.insertion,
					settings: f.insertion.JsonConfig.subTracksGenerationSettings || {}
				}));
			},
			async removeFile(file, idx) {
				let tracks = [...this.getTracks(file)];
				const remove = () => {
					this.excelData.splice(idx, 1);
					if (file.azureFile) {
						this.$azure.deleteBlob(file.azureFile.FileName, file.azureFile.AzureContainer);
					}
				}
				const removeSubTracks = () => {
					tracks.map(f => {
						this.$Data.getExternalApi('Creative/DeleteSubInsertions', `insertionId=${f.id}`).then(() => f.__loadSubTracks(true));
					});
				}
				this.$confirmMessage({
					title: this.$tCaption('Confirm action', '@actions'),
					text: this.$tCaption(`Do you want to remove "${file.name}"?`),
					buttons: [
						{
							text: this.$tCaption('Remove file and all sub-tracks'),
							type: 'danger',
							onClick: () => {
								removeSubTracks();
								remove();
							}
						},
						{
							text: this.$tCaption('Remove file only'),
							onClick: () => {
								remove();
							}
						},
						{
							text: this.$tCaption('Cancel'),
							type: 'normal'
						},
					]
				});
			},
			async onFileSelected(files) {
				const file = files[0];
				if (!file) {
					this.replaceDataState = 0;
					return;
				}

				if (this.replaceDataFile) {
					if (this.replaceDataState === 0) {
						this.columns = await readColumns(file);
						this.loadInsertionsInfo();

						let usedColumns = [];
						this.insertionsInfo.map(f => {
							if (f?.insertion?.JsonConfig?.subTracksGenerationSsmlTemplate) {
								getUsedColumnsInSsml(f.insertion.JsonConfig.subTracksGenerationSsmlTemplate, usedColumns);
							}
						});

						if (__DEBUG__)
							console.debug('Used columns', { usedColumns, columns: this.columns });

						let notExistsColumns = usedColumns.filter(f => !this.columns.includes(f));
						if (notExistsColumns.length === 0) {
							this.replaceDataState = 1;
						} else {
							this.$confirm(void 0, this.$tCaption(`Some used columns are absent in the new excel file: ${notExistsColumns.join(', ')}. Do you want to continue?`)).then(() => {
								this.replaceDataState = 1;
							}).catch(() => this.$refs.uploader.removeFile(0));
						}
					}
				}
			},
			async onFileUpload(files) {
				const file = files[0];
				if (!file) return;

				if (this.replaceDataFile) {
					this.$set(this.replaceDataFile, 'columns', _.cloneDeep(this.columns));

					if (!this.replaceData.keyColumn) {
						let keyColumnIndex = this.insertionsInfo.find(f => f.settings.keyColumnIndex != null)?.settings.keyColumnIndex;
						if (keyColumnIndex === null) {
							this.columns.findIndex(c => c == 'Address');
						}
						
						if (keyColumnIndex === -1) {
							keyColumnIndex = 0;
						}

						this.$set(this.replaceDataFile, 'keyColumnIndex', keyColumnIndex);
						this.$set(this.replaceDataFile, 'keyColumn', this.columns[keyColumnIndex]);
						this.$set(this.replaceDataFile, 'calcGeoLocation', this.insertionsInfo[0]?.settings?.calcGeoLocation || false);
					}

					this.processReplaceFileData(file);
					this.$refs.uploader.close();
					return;
				}

				this.isLoading++;
				const azureFile = (await this.$azure.uploadBlobWithInfoResponse(`creative_excel_data_${this.$Data.uuid()}.xlsx`, file).finally(() => this.isLoading--));

				let columns = await readColumns(file);

				let keyColumnIndex = columns.findIndex(c => c == 'Address');
				if (keyColumnIndex === -1) {
					keyColumnIndex = 0;
				}
				let keyColumn = columns[keyColumnIndex];
				
				const info = {
					id: this.$Data.uuid(),
					name: file.name,
					created: new Date(),
					insertions: [],
					azureFile,
					columns,
					calcGeoLocation: false,
					keyColumn,
					keyColumnIndex
				};

				this.excelData.push(info);
				this.$refs.uploader.close();
			},
			replaceData(file) {
				this.replaceDataFile = file;
				this.replaceDataState = 0;
				this.$refs.uploader.showPopup();
			},
			async processReplaceFileData(newBlob) {
				if (__DEBUG__)
					console.debug('Replace data', { file: this.replaceDataFile });
				let excelData = this.replaceDataFile;

				if (!excelData) return;

				if (excelData.azureFile) {
					this.$azure.deleteBlob(excelData.azureFile.FileName, excelData.azureFile.AzureContainer);
				}

				this.isLoading++;
				excelData.azureFile = await this.$azure.uploadBlobWithInfoResponse(`creative_excel_data_${this.$Data.uuid()}.xlsx`, newBlob).finally(() => this.isLoading--);
				excelData.updated = new Date();

				if (excelData.insertions?.length) {

					if (!this.insertionsInfo.length) return;

					let ssmlTemplateGroups = _.groupBy(this.insertionsInfo, f => {
						let keyColumnIndex = this.columns.findIndex(c => c == f.settings.keyColumn);
						if (keyColumnIndex === -1) {
							keyColumnIndex = this.columns.findIndex(c => c == 'Address');
						}
						return JSON.stringify({
							keyColumn: f.settings.keyColumn,
							keyColumnIndex: keyColumnIndex > -1 ? keyColumnIndex : f.settings.keyColumnIndex,
							calcGeoLocation: f.settings.calcGeoLocation
						});
					});

					this.$notifyMessage(
						this.$tCaption('The sub tracks you have uploaded are being processed. It will take some time.'),
						{ busy: true, type: 'warning' }
					);

					await Promise.all(Object.keys(ssmlTemplateGroups).map(async strVal => {
						const info = JSON.parse(strVal);
						const data = new FormData();
						let tracks = ssmlTemplateGroups[strVal].map(f => f.track);

						const importIntoInsertions = tracks.map(f => ({
							InsertionId: f.id,
							SsmlTemplate: ssmlHelper.prepareSsml(
								f.insertion.JsonConfig.subTracksGenerationSsmlTemplate,
								{
									...this.$Data.formatObjectToLowerCase(f.insertion),
									sender: this,
									isPreview: false
								}
							)?.ssml
						}));

						data.append('importIntoInsertions', JSON.stringify(importIntoInsertions));
						data.append('excelFile', newBlob);
						data.append('calcGeoLocation', info.calcGeoLocation);
						data.append('keyColumn', info.keyColumn);
						data.append('keyColumnIndex', info.keyColumnIndex);
						data.append('addToExisting', this.addToExisting);

						if (__DEBUG__)
							console.debug('Replace data', { data: [...data.values()], tracks });

						tracks.map(f => {
							f.__isLoading++;
							f.__isGeneratingSubInsertions = true;
						});
						return axios
							.post(
								this.$Data.getMethodUrl('postExternalApi?method=Creative/GenerateSubInsertions'),
								data,
								{
									headers: {
										'Content-Type': 'multipart/form-data',
										authToken: this.$auth.accessToken(),
										...__HEADERS__
									}
								})
							.then(({ data }) => {
								if (__DEBUG__)
									console.debug('GenerateSubInsertions result', { data });

								if (data.Success && data.Data) {
									tracks.map(track => {
										track.insertion.LastProcessingJob = {
											Oid: data.Data,
											Status: 0
										}
									});
								} else if (data.Success === false) {
									throw(data.ErrorMessage);
								}

								return data;
							})
							.catch((ex) => {
								this.$notifyMessage(ex, { fullScreen: true });
								tracks.map(f => {
									f.__isLoading--;
									f.__isGeneratingSubInsertions = false;
								});
								return false;
							});
					})).finally(() => {
						this.$eventsBus?.$emit('notification-busy', false);
					});
				}

				this.clear();
			},
			clear() {
				this.replaceDataFile = null;
			}
		}
	};
</script>
<style lang='scss'>
	.creative-excel-data {
	}
</style>
